import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AdapterHealth,
  AddNoteRequest,
  AlertDto,
  AssetDto,
  BriefDto,
  CompanyCardDto,
  CompanyDossierDto,
  CompanyResearchDto,
  ConferenceDto,
  DealDto,
  ExhibitorDto,
  PapersPageDto,
  DistressBoardRowDto,
  OverviewDto,
  PromptDto,
  QuartzJobDto,
  RadarPointDto,
  RejectReasonCode,
  RejectStatsDto,
  ReviewItemDto,
  RubricDto,
  RubricUpdateRequest,
  SetStageRequest,
  SourceDto,
  UsageMonthDto,
} from '../types';
import { resolveFixture } from '../fixtures';

// The M&A Radar fetch layer (WWD lib/api.ts pattern). Phase A serves typed fixtures;
// A6 flips USE_FIXTURES to false and every hook transparently hits the real MNA.Api
// through the portal BFF reverse-proxy (/api/mna/** -> MNA.Api /api/**) — same paths,
// same types.ts DTOs, zero component changes (the promised fetch-layer swap).

export const USE_FIXTURES = false;

export class MnaApiError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    super(message);
    this.name = 'MnaApiError';
  }
}

async function mnaFetch<T>(path: string, init?: RequestInit): Promise<T> {
  if (USE_FIXTURES) {
    // Microtask-deferred so loading states render exactly as they will against the Api.
    await Promise.resolve();
    try {
      return resolveFixture(path, init?.method ?? 'GET') as T;
    } catch (err) {
      const status = (err as { status?: number })?.status ?? 500;
      throw new MnaApiError(status, (err as Error).message);
    }
  }
  const res = await fetch(`/api/mna/${path}`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
    ...init,
  });
  if (!res.ok) throw new MnaApiError(res.status, res.statusText || `HTTP ${res.status}`);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

const STALE_MS = 60_000;
/** Brief + alerts refresh weekly/hourly server-side — 5 min client staleness is plenty. */
const BRIEF_ALERTS_STALE_MS = 300_000;

/** Don't retry 404s (e.g. unknown company slug) — they won't get better. */
function retryUnlessNotFound(failureCount: number, err: unknown): boolean {
  const status = (err as { status?: number })?.status;
  if (status === 404) return false;
  return failureCount < 2;
}

// ── Tab payloads ─────────────────────────────────────────────────────────────

export function useMnaOverview() {
  return useQuery({
    queryKey: ['mna', 'overview'],
    queryFn: () => mnaFetch<OverviewDto>('overview'),
    staleTime: STALE_MS,
  });
}

export function useMnaRadar() {
  return useQuery({
    queryKey: ['mna', 'radar'],
    queryFn: () => mnaFetch<RadarPointDto[]>('radar'),
    staleTime: STALE_MS,
  });
}

/** includeRetired: admin Companies view only — retired rows feed the collapsed "Retired" section. */
export function useMnaCompanies(includeRetired = false) {
  return useQuery({
    queryKey: ['mna', 'companies', { includeRetired }],
    queryFn: () => mnaFetch<CompanyCardDto[]>(`companies${includeRetired ? '?includeRetired=true' : ''}`),
    staleTime: STALE_MS,
  });
}

export function useMnaCompany(slug: string | undefined) {
  return useQuery({
    queryKey: ['mna', 'companies', slug],
    queryFn: () => mnaFetch<CompanyDossierDto>(`companies/${encodeURIComponent(slug ?? '')}`),
    enabled: slug !== undefined,
    staleTime: STALE_MS,
    retry: retryUnlessNotFound,
  });
}

export function useMnaAssets() {
  return useQuery({
    queryKey: ['mna', 'assets'],
    queryFn: () => mnaFetch<AssetDto[]>('assets'),
    staleTime: STALE_MS,
  });
}

export function useDistressBoard() {
  return useQuery({
    queryKey: ['mna', 'distress-board'],
    queryFn: () => mnaFetch<DistressBoardRowDto[]>('distress-board'),
    staleTime: STALE_MS,
  });
}

export function useDeals() {
  return useQuery({
    queryKey: ['mna', 'deals'],
    queryFn: () => mnaFetch<DealDto[]>('deals'),
    staleTime: STALE_MS,
  });
}

/** Latest brief for a window (24h/7d/30d). 404 (none generated yet) is a valid empty state. */
export function useBrief(window: '24h' | '7d' | '30d' = '7d') {
  return useQuery({
    queryKey: ['mna', 'brief', window],
    queryFn: () => mnaFetch<BriefDto | null>(`brief?window=${window}`),
    staleTime: BRIEF_ALERTS_STALE_MS,
    retry: retryUnlessNotFound,
  });
}

/** Relevancy-sorted conference papers (Crossref-fed; conference intelligence). */
export function usePapers(minScore: number, conference: string, company: string, page: number) {
  const params = new URLSearchParams();
  if (minScore > 0) params.set('minScore', String(minScore));
  if (conference) params.set('conference', conference);
  if (company) params.set('company', company);
  if (page > 0) params.set('page', String(page));
  const qs = params.toString();
  return useQuery({
    queryKey: ['mna', 'papers', minScore, conference, company, page],
    queryFn: () => mnaFetch<PapersPageDto>(`papers${qs ? `?${qs}` : ''}`),
    staleTime: STALE_MS,
  });
}

/** B2 — conference exhibitor rows for one company (dossier "Conference presence" card). */
export function useExhibitors(company: string) {
  return useQuery({
    queryKey: ['mna', 'exhibitors', company],
    queryFn: () => mnaFetch<ExhibitorDto[]>(`exhibitors?company=${encodeURIComponent(company)}`),
    staleTime: STALE_MS,
  });
}

/** Paper-research verdicts (papers → companies → candidates; Discovery tab). */
export function useResearch(verdict: string) {
  const qs = verdict ? `?verdict=${encodeURIComponent(verdict)}` : '';
  return useQuery({
    queryKey: ['mna', 'research', verdict],
    queryFn: () => mnaFetch<CompanyResearchDto[]>(`research${qs}`),
    staleTime: STALE_MS,
  });
}

/** Upcoming IADC/SPE conferences (awareness calendar). */
export function useConferences() {
  return useQuery({
    queryKey: ['mna', 'conferences'],
    queryFn: () => mnaFetch<ConferenceDto[]>('conferences'),
    staleTime: STALE_MS,
  });
}

export function useAlerts() {
  return useQuery({
    queryKey: ['mna', 'alerts'],
    queryFn: () => mnaFetch<AlertDto[]>('alerts'),
    staleTime: BRIEF_ALERTS_STALE_MS,
  });
}

export function useMnaSources() {
  return useQuery({
    queryKey: ['mna', 'sources'],
    queryFn: () => mnaFetch<SourceDto[]>('sources'),
    staleTime: STALE_MS,
  });
}

export function useMnaPrompts() {
  return useQuery({
    queryKey: ['mna', 'prompts'],
    queryFn: () => mnaFetch<PromptDto[]>('prompts'),
    staleTime: STALE_MS,
  });
}

// ── Workflow (Phase D — stage transitions + notes) ──────────────────────────
// Writes are admin-gated server-side (the BFF's trusted header carries the role);
// the dossier only renders the inputs for admins, mirroring the Debug-tab gate.

/** JSON-body POST helper — mnaFetch spreads init last, so restate Accept alongside Content-Type. */
function mnaPost<T>(path: string, body: unknown): Promise<T> {
  return mnaFetch<T>(path, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export function useSetStage(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SetStageRequest) =>
      mnaPost<unknown>(`companies/${encodeURIComponent(slug)}/stage`, body),
    // Stage shows on the dossier, the company cards, the radar and the overview counts.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mna', 'companies'] });
      qc.invalidateQueries({ queryKey: ['mna', 'overview'] });
      qc.invalidateQueries({ queryKey: ['mna', 'radar'] });
    },
  });
}

export function useAddNote(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AddNoteRequest) =>
      mnaPost<unknown>(`companies/${encodeURIComponent(slug)}/notes`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mna', 'companies', slug] }),
  });
}

// ── Retirement (Track A — admin-only; projection flag, events/scores retained) ──

/** Retire or un-retire a company. Touches scoring + every surface — invalidate the tenant. */
export function useRetireCompany(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ action, note }: { action: 'retire' | 'unretire'; note?: string }) =>
      mnaPost<unknown>(`admin/companies/${encodeURIComponent(slug)}/${action}`, { note: note ?? null }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mna'] }),
  });
}

// ── Rubric (Phase D — admin-only editor; versioned config, ADR-0009 §3) ─────

export function useRubric() {
  return useQuery({
    queryKey: ['mna', 'admin', 'rubric'],
    queryFn: () => mnaFetch<RubricDto>('admin/rubric'),
  });
}

export function useRubricVersions() {
  return useQuery({
    queryKey: ['mna', 'admin', 'rubric', 'versions'],
    queryFn: () => mnaFetch<RubricDto[]>('admin/rubric/versions'),
  });
}

/** Save a new rubric version. Server assigns vN+1, marks it active and queues the re-score pass. */
export function useSaveRubric() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RubricUpdateRequest) => mnaPost<RubricDto>('admin/rubric', body),
    // Prefix-invalidates both the active rubric and the versions list.
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mna', 'admin', 'rubric'] }),
  });
}

// ── Admin / Debug (portal-admin only — the BFF adds the trusted header) ──────

/** Trigger response for the manual Run buttons (mirrors WWD's TriggerResultDto — an
 *  admin-plumbing shape, deliberately not part of the types.ts data contract). */
export interface TriggerResultDto {
  success: boolean;
  message: string;
}

const DEBUG_REFETCH_MS = 8_000;

export function useMnaReviewQueue(enabled: boolean) {
  return useQuery({
    queryKey: ['mna', 'admin', 'review'],
    queryFn: () => mnaFetch<ReviewItemDto[]>('admin/review'),
    enabled,
  });
}

export function useMnaReviewAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      action,
      reasonCode,
      note,
    }: {
      id: string;
      action: 'approve' | 'reject';
      reasonCode?: RejectReasonCode;
      note?: string;
    }) =>
      mnaPost<unknown>(`admin/review/${encodeURIComponent(id)}/${action}`, {
        note: note ?? null,
        reasonCode: reasonCode ?? null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mna', 'admin', 'review'] });
      qc.invalidateQueries({ queryKey: ['mna', 'admin', 'reject-stats'] });
    },
  });
}

/** B4 — reject reason-code counts (30/90d) + recent names, for the review-tab stats table. */
export function useMnaRejectStats(enabled: boolean) {
  return useQuery({
    queryKey: ['mna', 'admin', 'reject-stats'],
    queryFn: () => mnaFetch<RejectStatsDto>('admin/review/reject-stats'),
    enabled,
  });
}

export function useMnaQuartz(enabled: boolean) {
  return useQuery({
    queryKey: ['mna', 'admin', 'quartz'],
    queryFn: () => mnaFetch<QuartzJobDto[]>('admin/debug/quartz'),
    enabled,
    refetchInterval: enabled && !USE_FIXTURES ? DEBUG_REFETCH_MS : false,
    refetchIntervalInBackground: false,
  });
}

export function useMnaRunJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (key: string) =>
      mnaFetch<TriggerResultDto>(`admin/debug/triggers/${encodeURIComponent(key)}`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mna', 'admin', 'quartz'] }),
  });
}

export function useMnaAdapterHealth(enabled: boolean) {
  return useQuery({
    queryKey: ['mna', 'admin', 'adapters'],
    queryFn: () => mnaFetch<AdapterHealth[]>('admin/debug/adapters'),
    enabled,
    refetchInterval: enabled && !USE_FIXTURES ? DEBUG_REFETCH_MS : false,
    refetchIntervalInBackground: false,
  });
}

export function useMnaUsage(month: string, enabled: boolean) {
  return useQuery({
    queryKey: ['mna', 'admin', 'usage', month],
    queryFn: () => mnaFetch<UsageMonthDto>(`admin/usage?month=${encodeURIComponent(month)}`),
    enabled,
  });
}
